import { useEffect, useMemo, useState } from 'react'
import { View, Text, StyleSheet, Platform } from 'react-native'
import Svg, { Defs, LinearGradient, Stop, Path, Polygon, Circle, Line, G, Rect, Text as SvgText } from 'react-native-svg'
import Animated, { Easing, cancelAnimation, useAnimatedProps, useReducedMotion, useSharedValue, withRepeat, withTiming } from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { legibleOn } from '../../lib/contrast'
import { darken, lighten } from '../personAvatarConfig'
import ClimbDetail from './ClimbDetail'

// Profilo altimetrico di una tappa, disegnato dai dati (dim_race_stage.profile, jsonb) con i colori dell'app: nessuna immagine.
// Formato v1: { v, km_tot, curva: [[km, quota m], ...] a passo ~1 km, partenza: {nome, quota}, arrivo: {nome, quota},
//               salite: [{ da, a, pend, nome, cima, lung, coppi? }], fonte }
// Le salite si colorano per pendenza media; il tinto e' quello della competizione (rosa Giro di default).
// Se la tappa non ha un profilo pubblicato il componente non mostra niente. Lo si carica da admin > Palinsesto.

const cache = new Map()      // id_tappa -> profilo | null (non cambia durante la sessione)

async function loadProfile(idStage) {
  if (cache.has(idStage)) return cache.get(idStage)
  try {
    const { data, error } = await supabase.from('dim_race_stage').select('profile').eq('id_stage', idStage).maybeSingle()
    if (error) throw error
    const p = data?.profile?.curva?.length > 1 ? data.profile : null
    cache.set(idStage, p)
    return p
  } catch {
    return null              // colonna non ancora presente o rete assente: nessun profilo, nessun errore a schermo
  }
}

const PAD_L = 34, PAD_R = 8, PAD_B = 22, INNER_H = 100
const MARK_W = 18, MARK_H = 16         // triangolo del GPM
const LEVEL_H = 30                     // un GPM vicino al precedente sale di un piano
const MIN_DX = 26                      // distanza minima (px) tra due marcatori sullo stesso piano
const MAX_LEVEL = 3
const AnimatedCircle = Animated.createAnimatedComponent(Circle)

// Marcatori: solo i GPM sono "grandi" e si toccano (scheda della salita); sprint e bonus sono segni piccoli sulla curva.
// Nessun testo nel grafico: il nome sta nella scheda. Se due GPM sono vicini il secondo sale di un piano, con il suo stelo.
function layoutClimbs(salite, x) {
  const items = salite.map((s, i) => ({ s, i, cx: x(s.a), level: 0 })).sort((p, q) => p.cx - q.cx)
  const last = []                      // ultimo cx per piano
  for (const it of items) {
    let lv = 0
    while (lv < MAX_LEVEL - 1 && last[lv] != null && it.cx - last[lv] < MIN_DX) lv++
    it.level = lv
    last[lv] = it.cx
  }
  return items
}
// scala delle quote a sinistra: passo "tondo" con al massimo 5 tacche
const niceStep = (max) => [100, 200, 250, 500, 1000].find(st => max / st <= 5) ?? 1000

const num = (n, lang, digits = 0) => {
  try { return Number(n).toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits }) }
  catch { return Number(n).toFixed(digits).replace('.', ',') }
}

export default function StageProfile({ idStage }) {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [p, setP] = useState(() => cache.get(idStage) ?? null)
  const [w, setW] = useState(0)
  const [climbOpen, setClimbOpen] = useState(null)   // salita aperta nel dettaglio
  // anello tratteggiato che gira intorno ai GPM per qualche secondo ogni volta che il profilo compare: invita a toccarli.
  // Fermo con "riduci animazioni" e sul web.
  const reduceMotion = useReducedMotion()
  const dash = useSharedValue(0)
  const [spin, setSpin] = useState(false)
  useEffect(() => {
    if (!p || reduceMotion || Platform.OS === 'web') return undefined
    setSpin(true)
    dash.value = 0
    dash.value = withRepeat(withTiming(18, { duration: 2400, easing: Easing.linear }), -1, false)
    const timer = setTimeout(() => { cancelAnimation(dash); setSpin(false) }, 4800)
    return () => { clearTimeout(timer); cancelAnimation(dash) }
  }, [p, idStage, reduceMotion, dash])
  const ringProps = useAnimatedProps(() => ({ strokeDashoffset: dash.value }))
  const openClimb = (s) => setClimbOpen(s)

  useEffect(() => {
    let cancelled = false
    setP(cache.get(idStage) ?? null)
    if (idStage) loadProfile(idStage).then(v => { if (!cancelled) setP(v) })
    return () => { cancelled = true }
  }, [idStage])

  const brand = tc.rosa
  const SLOPE = useMemo(() => ({ lo: lighten(brand, 0.45), mid: brand, hi: darken(brand, 0.7) }), [brand])
  const slopeKey = (pend) => (pend < 6 ? 'lo' : pend < 9 ? 'mid' : 'hi')
  const lang = i18n.language

  const geo = useMemo(() => {
    if (!p || !w) return null
    const x = (km) => PAD_L + (km / p.km_tot) * (w - PAD_L - PAD_R)
    const max = Math.max(...p.curva.map(c => c[1]))
    const step = niceStep(max)
    const yMax = Math.ceil(max / step) * step          // la scala arriva alla tacca tonda sopra il punto piu' alto (es. 748 -> 800)
    const curveAt = (km) => { let b = p.curva[0]; for (const c of p.curva) if (Math.abs(c[0] - km) < Math.abs(b[0] - km)) b = c; return b[1] }
    const climbs = layoutClimbs(p.salite ?? [], x)
    // altezza in alto: deve starci il marcatore piu' alto (quota della cima + stelo + piani)
    let padT = 12
    for (const it of climbs) {
      const need = 40 + it.level * LEVEL_H - INNER_H * (1 - it.s.cima / yMax)
      if (need > padT) padT = need
    }
    const y = (m) => padT + INNER_H - (m / yMax) * INNER_H
    const Ht = padT + INNER_H + PAD_B
    const pts = p.curva.map(([km, m]) => [x(km), y(m)])
    const line = pts.map((q, i) => `${i ? 'L' : 'M'}${q[0].toFixed(1)} ${q[1].toFixed(1)}`).join(' ')
    const base = padT + INNER_H
    const area = `${line} L${pts[pts.length - 1][0].toFixed(1)} ${base} L${pts[0][0].toFixed(1)} ${base} Z`
    const seg = (s) => p.curva.filter(([km]) => km >= s.da && km <= s.a)
      .map(([km, m], i) => `${i ? 'L' : 'M'}${x(km).toFixed(1)} ${y(m).toFixed(1)}`).join(' ')
    const ticks = []
    for (let k = 0; k < p.km_tot; k += 50) ticks.push(k)
    const yTicks = []
    for (let v = 0; v <= yMax; v += step) yTicks.push(v)
    return { H: Ht, max, x, y, onCurve: curveAt, line, area, seg, ticks, yTicks, climbs }
  }, [p, w])

  if (!p) return null

  const salite = p.salite ?? []
  const maxQ = Math.max(...p.curva.map(c => c[1]))
  const labelCol = legibleOn(brand, tc.bgCard, 3)
  const a11y = t('pick.profile_a11y', { km: num(p.km_tot, lang, 1), max: num(maxQ, lang), n: salite.length })

  return (
    <View style={styles.card} accessible accessibilityRole="image" accessibilityLabel={a11y}>
      <View style={styles.head}>
        <Text style={styles.title}>{t('pick.profile_title')}</Text>
        <Text style={styles.km}>{num(p.km_tot, lang, 1)} km</Text>
      </View>

      <View onLayout={e => setW(e.nativeEvent.layout.width)} style={{ height: geo?.H ?? 190 }}>
        {geo && (
          <Svg width={w} height={geo.H}>
            <Defs>
              <LinearGradient id="spFill" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor={brand} stopOpacity="0.42" />
                <Stop offset="1" stopColor={brand} stopOpacity="0.02" />
              </LinearGradient>
            </Defs>

            {geo.yTicks.map(v => (
              <G key={`y${v}`}>
                <Line x1={PAD_L} x2={w - PAD_R} y1={geo.y(v)} y2={geo.y(v)} stroke={tc.border} strokeWidth="0.8" />
                <SvgText x={PAD_L - 6} y={geo.y(v) + 4} fontSize="11" fontFamily={fonts.condensedRegular} fill={tc.textMuted} textAnchor="end">{v}</SvgText>
              </G>
            ))}
            <Path d={geo.area} fill="url(#spFill)" />
            <Path d={geo.line} stroke={tc.textMuted} strokeWidth="1.8" fill="none" strokeLinejoin="round" strokeLinecap="round" />

            {salite.map((s, i) => (
              <Path key={`h${i}`} d={geo.seg(s)} stroke={tc.bgCard} strokeWidth="6" fill="none" strokeLinejoin="round" strokeLinecap="round" />
            ))}
            {salite.map((s, i) => (
              <Path key={`c${i}`} d={geo.seg(s)} stroke={SLOPE[slopeKey(s.pend)]} strokeWidth="3.4" fill="none" strokeLinejoin="round" strokeLinecap="round" />
            ))}

            {/* partenza / arrivo */}
            {[[0, p.curva[0][1]], [p.km_tot, p.curva[p.curva.length - 1][1]]].map(([km, m], i) => (
              <Circle key={`e${i}`} cx={geo.x(km)} cy={geo.y(m)} r="3.6" fill={tc.bgCard} stroke={tc.text} strokeWidth="1.6" />
            ))}

            {/* sprint e bonus: solo un puntino sulla curva (non si toccano) */}
            {[...(p.sprint ?? []), ...(p.bonus ?? [])].map((m, i) => (
              <Circle key={`m${i}`} cx={geo.x(m.km)} cy={geo.y(geo.onCurve(m.km))} r="3" fill={tc.bgCard} stroke={tc.text} strokeWidth="1.4" />
            ))}

            {/* GPM: marcatore con la categoria, stelo fino alla curva, anello che invita al tocco; piani diversi se vicini */}
            {geo.climbs.map(({ s, i, cx, level }) => {
              const cyCurve = geo.y(s.cima)
              const mx = Math.min(Math.max(cx, 16), w - 16)           // il marcatore non esce dal grafico (arrivo in salita al bordo destro)
              const my = cyCurve - 12 - level * LEVEL_H             // base del marcatore
              const mc = my - MARK_H / 2                            // centro
              return (
                <G key={`c${i}`} onPress={() => openClimb(s)} accessible accessibilityRole="button" accessibilityLabel={`${s.nome}, ${num(s.lung, lang, 1)} km, ${num(s.pend, lang, 1)}%`}>
                  <Line x1={cx} x2={mx} y1={cyCurve - 3} y2={my} stroke={SLOPE[slopeKey(s.pend)]} strokeWidth="1.4" />
                  <Circle cx={cx} cy={cyCurve} r="3" fill={SLOPE[slopeKey(s.pend)]} stroke={tc.bgCard} strokeWidth="1.2" />
                  {s.coppi
                    ? <Circle cx={mx} cy={mc} r="9" fill={brand} stroke={tc.bgCard} strokeWidth="1.4" />
                    : <Polygon points={`${mx - MARK_W / 2},${my} ${mx + MARK_W / 2},${my} ${mx},${my - MARK_H}`} fill={tc.text} stroke={tc.bgCard} strokeWidth="1.2" strokeLinejoin="round" />}
                  <SvgText x={mx} y={s.coppi ? mc + 3.2 : my - 3} fontSize={s.coppi ? 9 : 10} fontFamily={fonts.condensedBold} fill={s.coppi ? '#FFFFFF' : tc.bgCard} textAnchor="middle">{s.coppi ? 'C' : (s.cat === 0 ? 'HC' : s.cat ?? '')}</SvgText>
                  <AnimatedCircle cx={mx} cy={mc} r="14" fill="none" stroke={brand} strokeWidth="1.2" strokeDasharray="4 3.5" animatedProps={ringProps} opacity={spin ? 0.95 : 0.55} />
                  <Rect x={mx - 18} y={mc - 17} width="36" height="34" fill="transparent" />
                </G>
              )
            })}

            {/* asse dei chilometri */}
            {geo.ticks.map(k => (
              <SvgText key={`k${k}`} x={geo.x(k)} y={geo.H - 6} fontSize="12" fontFamily={fonts.condensedRegular} fill={tc.textMuted} textAnchor={k === 0 ? 'start' : 'middle'}>{k}</SvgText>
            ))}
          </Svg>
        )}
      </View>

      <View style={styles.legend}>
        <Text style={styles.legendText}>{t('pick.profile_climbs')}</Text>
        {[['lo', '3–6%'], ['mid', '6–9%'], ['hi', '> 9%']].map(([k, label]) => (
          <View key={k} style={styles.chip}>
            <View style={[styles.swatch, { backgroundColor: SLOPE[k] }]} />
            <Text style={styles.legendText}>{label}</Text>
          </View>
        ))}
        {((p.sprint ?? []).length > 0 || (p.bonus ?? []).length > 0) && (
          <View style={[styles.chip, { marginLeft: 'auto' }]}>
            <View style={[styles.dot, { borderColor: tc.text, backgroundColor: tc.bgCard }]} />
            <Text style={styles.legendText}>{t('pick.profile_sprint_bonus')}</Text>
          </View>
        )}
      </View>
      {salite.length > 0 && <Text style={styles.source}>{t('pick.profile_tap_climb')}{salite.some(s => s.coppi) ? ` · ${t('pick.profile_coppi')}` : ''}</Text>}
      {climbOpen && <ClimbDetail climb={climbOpen} onClose={() => setClimbOpen(null)} />}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  card: { backgroundColor: tc.bgCard, borderColor: tc.border, borderWidth: 1, borderRadius: 14, paddingHorizontal: 12, paddingTop: 10, paddingBottom: 10, marginHorizontal: 16, marginTop: 4, marginBottom: 12 },
  head: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  title: { fontFamily: fonts.condensedBold, fontSize: 15, color: tc.text, textTransform: 'uppercase', letterSpacing: 0.5 },
  km: { fontFamily: fonts.condensedBold, fontSize: 15, color: tc.text },
  legend: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 4 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  swatch: { width: 14, height: 8, borderRadius: 3 },
  dot: { width: 8, height: 8, borderRadius: 4, borderWidth: 1.4 },
  legendText: { fontSize: 12, color: tc.textMuted },
  source: { fontSize: 12, color: tc.textMuted, marginTop: 4 },
})
